"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { FilterVariantCount } from "@/components/ui/filters";
import type { VariantFilterValue } from "@/lib/api/types";
import { formatEntityForms, type VariantField } from "@/lib/search/entity-variants";
import { logo } from "@/lib/logo-dev";
import { titleCasePreserveAcronyms } from "@/lib/string/title-case";
import { cn } from "@/lib/utils";
import { FilterValueActions } from "../actions";
import { ScopeDropdown } from "./internal/scope-dropdown";
import { VariantsDropdown } from "./internal/variants-dropdown";

export function FilterUniversityInput({
  university,
  variantsField,
  onChange,
  onRemove,
}: {
  university: VariantFilterValue;
  /** Opt-in: lets the chip carry alias variants matched from this field. */
  variantsField?: VariantField;
  onChange: (updated: VariantFilterValue) => void;
  onRemove: () => void;
}) {
  const name = titleCasePreserveAcronyms(university.text ?? "");
  const scope = university.scope ?? "cp";
  const isExcluded = university.mode === "exclude";

  const logoUrl = logo({ domain: university.domain, name });

  return (
    <div
      className={cn(
        "border rounded-md overflow-clip",
        isExcluded ? "border-destructive/30" : "border-border",
      )}
    >
      <div className="flex items-center justify-between bg-contrast/4 h-[34px] px-2">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          {logoUrl && (
            <Avatar className="size-4 shrink-0 rounded-[3px] shadow-[0px_0.35px_0.7px_0px_rgba(0,0,0,0.05)]">
              <AvatarImage src={logoUrl} alt="" />
              <AvatarFallback />
              {/* Inner rim sits above the image (inset shadows are painted
                  below element content, so we overlay it instead). */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0px_-0.35px_0px_0px_rgba(0,0,0,0.08)]"
              />
            </Avatar>
          )}
          <span
            title={university.variants?.length ? formatEntityForms({ text: name, variants: university.variants }) : undefined}
            className={cn(
              "text-[13px] font-normal truncate tracking-[-0.13px]",
              isExcluded
                ? "text-destructive/80 dark:text-red-400/80 line-through"
                : "text-foreground/80",
            )}
          >
            {name}
            <FilterVariantCount count={university.variants?.length ?? 0} />
          </span>
        </div>
        <FilterValueActions
          excluded={isExcluded}
          required={university.required ?? false}
          onToggleRequired={() =>
            onChange({ ...university, required: !university.required })
          }
          onToggleExclude={() =>
            onChange({
              ...university,
              mode: isExcluded ? "include" : "exclude",
            })
          }
          onRemove={onRemove}
        />
      </div>
      <div className="flex items-center justify-end gap-3 bg-contrast/8 border-t border-border h-7 px-2">
        {variantsField && (
          <VariantsDropdown field={variantsField} item={university} onChange={onChange} />
        )}
        <ScopeDropdown
          value={scope}
          onChange={(v) => onChange({ ...university, scope: v })}
        />
      </div>
    </div>
  );
}
